import { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchRecentBookings, selectRecentBookings, selectRecentBookingsStatus } from '../../redux/recentBookingsSlice';
import { fetchAllFacilities } from '../../redux/facilitySlice';
import {
  fetchFacilityDailyReport,
  selectFacilityDailyReport,
  selectFacilityDailyReportLoading
} from '../../redux/facilityDailyReportSlice';
import './home.css';

// Import your SVG icons
import calendarIcon from '../../assets/svgs/home-time.svg';
import manageIcon from '../../assets/svgs/manage.svg';
import reportIcon from '../../assets/svgs/home-report.svg';
import reportIcon2 from '../../assets/svgs/reportIcon.svg';
// Header is now in MainLayout, no need to import here
import moment from 'moment';
import priceIcon from '../../assets/svgs/price-icon.svg';
import dateIcon from '../../assets/svgs/date-icon.svg';
import clockIcon from '../../assets/svgs/clock-min-icon.svg';
import facilityflag from "../../assets/sidepanel/flag.svg";
import sportsicon from "../../assets/sidepanel/sports.svg";
import pitch from "../../assets/sidepanel/pitch.svg";
import SportBackground from '../../assets/covers/sportbg.jpg';

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const recentBookings = useSelector(selectRecentBookings);
  const bookingStatus = useSelector(selectRecentBookingsStatus);

  // Add state for filters
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));

  // Get facilities from Redux
  const { facilities } = useSelector((state) => state.facilities);

  // Get facility daily report from Redux
  const reportData = useSelector(selectFacilityDailyReport);
  // const reportStatus = useSelector(selectFacilityDailyReportStatus);
  const reportLoading = useSelector(selectFacilityDailyReportLoading);


  // Fetch recent bookings and facilities on component mount
  useEffect(() => {
    dispatch(fetchRecentBookings());
    dispatch(fetchAllFacilities());
  }, [dispatch]);


  // Debug logging
  useEffect(() => {
  }, [recentBookings, bookingStatus]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD-MMM-YYYY');
  };

  // Helper function to format time range
  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    return `${startTime} - ${endTime}`;
  };

  // Handle view all bookings
  const handleViewAllBookings = () => {
    navigate('/bookings');
  };

  // Handle facility selection
  const handleFacilityChange = (facilityId) => {
    setSelectedFacility(facilityId);
    if (facilityId && selectedDate) {
      // Convert date to MM-DD-YYYY format for API
      const formattedDate = moment(selectedDate).format('MM-DD-YYYY');
      dispatch(fetchFacilityDailyReport({ facilityId, date: formattedDate }));
    }
  };

  // Handle date selection
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (selectedFacility && date) {
      // Convert date to MM-DD-YYYY format for API
      const formattedDate = moment(date).format('MM-DD-YYYY');
      dispatch(fetchFacilityDailyReport({ facilityId: selectedFacility, date: formattedDate }));
    }
  };


  return (
    <>
      <div className="home-container">
        {/* Header is now in MainLayout */}
        <div className="header-section">
          <div className="header-content">
            <div className="welcome-section">
              <h1 style={{ fontSize: '28px' }} className="blink-text">Welcome Back 👋 </h1>
              <p style={{ fontSize: '17px' }}>Manage your facilities efficiently & grow your business</p>
            </div>
            <img className="header-sport-bg" src={SportBackground} alt="Sport" />

          </div>
        </div>

        {/* Feature Cards */}
        <div className="features-section">
          <div className="feature-card green">
            <div className="icon-circle">
              <img style={{ width: '95%', height: '95%' }} src={calendarIcon} alt="Calendar" />
            </div>
            <h3>Schedule</h3>
            <p>View, edit and manage your facility schedule</p>
            <button onClick={() => navigate('/schedule')} className="discover-more">
              Discover More <span className='arrow-icon-home'>→</span>
            </button>
          </div>

          <div className="feature-card blue">
            <div className="icon-circle">
              <img style={{ width: '95%', height: '95%' }} src={manageIcon} alt="Clock" />
            </div>
            <h3>Manage Slots</h3>
            <p>Create, edit, or delete time slots for all your facilities</p>
            <button onClick={() => navigate('/slots')} className="manage">
              Manage <span className='arrow-icon-home'>→</span>
            </button>
          </div>

          <div className="feature-card green">
            <div className="icon-circle">
              <img style={{ width: '100%', height: '100%' }} src={reportIcon2} alt="Report" />
            </div>
            <h3>View Reports</h3>
            <p>View and download reports on facility bookings.</p>
            <button onClick={() => navigate('/analytics')} className="discover-more">
              Discover More <span className='arrow-icon-home'>→</span>
            </button>
          </div>

          <div className="feature-card pink">
            <div className="icon-circle">
              <img style={{ width: '100%', height: '100%' }} src={reportIcon} alt="Report" />
            </div>
            <h3>View Dashboard</h3>
            <p>Analyze performance, revenue, and booking trends</p>
            <button onClick={() => navigate('/dashboard')} className="discover-more">
              Discover More <span className='arrow-icon-home'>→</span>
            </button>
          </div>
        </div>

        {/* Dashboard Section */}
        <div className="dashboard-section">
          {/* Recent Bookings */}
          <div className="bookings-section">
            <div className="section-header">
              <h2>Recent Bookings</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  className="refresh-button"
                  onClick={() => dispatch(fetchRecentBookings())}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.453 12.893C19.604 16.9394 15.9619 19.8761 11.7779 19.4814C7.59387 19.0866 4.40614 15.7328 4.19489 11.5329C3.98363 7.33303 6.86338 3.58859 10.8927 2.60881C14.922 1.62902 19.0912 3.68241 20.453 7.57103" stroke="#38B000" strokeWidth="2" strokeLinecap="round" />
                    <path d="M20.5 3V8H15.5" stroke="#38B000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button className="view-all" onClick={handleViewAllBookings}>View All →</button>
              </div>
            </div>

            <div className="booking-items">
              {bookingStatus === 'loading' ? (
                // Loading state with skeletons
                <>
                  <div className="booking-item skeleton-booking">
                    <div className="booking-info">
                      <div className="skeleton-name"></div>
                      <div className="skeleton-sport"></div>
                      <div className="booking-meta">
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-meta"></div>
                      </div>
                    </div>
                  </div>
                  <div className="booking-item skeleton-booking">
                    <div className="booking-info">
                      <div className="skeleton-name"></div>
                      <div className="skeleton-sport"></div>
                      <div className="booking-meta">
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-meta"></div>
                      </div>
                    </div>
                  </div>
                  <div className="booking-item skeleton-booking">
                    <div className="booking-info">
                      <div className="skeleton-name"></div>
                      <div className="skeleton-sport"></div>
                      <div className="booking-meta">
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-meta"></div>
                        <div className="skeleton-meta"></div>
                      </div>
                    </div>
                  </div>
                </>
              ) : recentBookings.length > 0 ? (
                // Display actual bookings
                recentBookings.map((booking, index) => {
                  return (
                    <div key={booking._id || index} className="booking-item">
                      <div className="booking-info">
                        <div className="booking-header">
                          <h4>{booking.userName || 'N/A'}</h4>
                          <span className="status-badge">{`Booking ID: ${booking.originalBooking.activityId.bookingId}`}</span>
                        </div>
                        <div style={{ marginBottom: '10px' }} className="booking-meta">
                          {/* <p className="sport-info"> */}
                          <div className="meta-item">
                            <img src={facilityflag} alt="Facility" />
                            <span className="">{booking.facility}</span>
                          </div>
                          <div className='meta-item'>
                            <img src={sportsicon} alt="sports" />
                            <span className="sport-pitch">{booking.sport}</span>
                          </div>
                          <div className='meta-item'>
                            <img src={pitch} alt="pitch" />
                            <span className="sport-pitch">{booking.pitch}</span>
                          </div>
                          {/* </p> */}
                        </div>
                        <div className="booking-meta">
                          <div className="meta-item">
                            <img src={dateIcon} alt="date" />
                            <span>{formatDate(booking.date)}</span>
                          </div>
                          <div className="meta-item">
                            <img src={clockIcon} alt="time" />
                            <span>{formatTimeRange(booking.startTime, booking.endTime)}</span>
                          </div>
                          <div className="meta-item">
                            <img src={priceIcon} alt="price" />
                            <span>PKR {booking.price}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                // No bookings state
                <div className="booking-item">
                  <div className="booking-info">
                    <h4>No Recent Bookings</h4>
                    <p className="sport-info">No bookings found</p>
                    <div className="booking-meta">
                      <span>-</span>
                      <span>-</span>
                      <span>-</span>
                    </div>
                  </div>
                  <span className="status-badge">No Data</span>
                </div>
              )}
            </div>
          </div>

          {/* Slots Info */}
          <div className="slots-section">
            <div className="section-header">
              <div className="title-with-refresh">
                <h2>Slots Info</h2>
                <button
                  className="refresh-button-slots"
                  onClick={() => {
                    // Always fetch with current selections
                    const formattedDate = moment(selectedDate).format('MM-DD-YYYY');
                    const facilityId = selectedFacility || ''; // Send empty string if no facility selected
                    dispatch(fetchFacilityDailyReport({ facilityId, date: formattedDate }));
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.453 12.893C19.604 16.9394 15.9619 19.8761 11.7779 19.4814C7.59387 19.0866 4.40614 15.7328 4.19489 11.5329C3.98363 7.33303 6.86338 3.58859 10.8927 2.60881C14.922 1.62902 19.0912 3.68241 20.453 7.57103" stroke="#38B000" strokeWidth="2" strokeLinecap="round" />
                    <path d="M20.5 3V8H15.5" stroke="#38B000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Dropdown className="facility-filter">
                  <Dropdown.Toggle variant="light" id="facility-dropdown">
                    {selectedFacility ?
                      facilities.find(f => f._id === selectedFacility)?.name || 'Select Facility'
                      : 'Select Facility'
                    }
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {/* <Dropdown.Item onClick={() => handleFacilityChange('')}>
                      All Facilities
                    </Dropdown.Item> */}
                    {facilities.map((facility) => (
                      <Dropdown.Item
                        key={facility._id}
                        onClick={() => handleFacilityChange(facility._id)}
                      >
                        {facility.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <input
                  type="date"
                  className="date-filter"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                />
              </div>
            </div>

            <div className="stats-grid">
              {reportLoading ? (
                // Loading state for all cards
                <>
                  <div className="stat-card">
                    <div className="stat-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  </div>
                </>
              ) : reportData ? (
                // Display actual data
                <>
                  <div className="stat-card">
                    <h3>Slots Booked</h3>
                    <div className="stat-number">
                      {reportData.summary?.bookedSlots || 0}<span className="stat-total">/{reportData.summary?.totalSlots || 0}</span>
                    </div>
                    <div className="stat-breakdown">
                      {reportData.sportsBreakdown?.slice(0, 2).map((sport, index) => (
                        <span key={sport.sportId} className="stat-item">
                          {sport.bookedSlots} {sport.sportName}
                        </span>
                      ))}
                    </div>
                    {/* <p className="stat-note">Occupancy: {reportData.summary?.occupancyRate || 0}%</p> */}
                    <p className="revenue-increase">Occupancy: {reportData.summary?.occupancyRate || 0}%</p>
                  </div>

                  <div className="stat-card">
                    <h3>Top Sport</h3>
                    <div className="stat-number">
                      {reportData.performanceMetrics?.topPerformingSport?.name}
                    </div>
                    <div className="stat-breakdown">
                      <span className="stat-item">
                        Slots Booked: {reportData.sportsBreakdown.find(sport => sport.sportName === reportData.performanceMetrics?.topPerformingSport?.name)?.bookedSlots || 0}
                      </span>
                    </div>
                    <p className="revenue-increase">Revenue: {reportData.performanceMetrics?.topPerformingSport?.revenue || 0} PKR</p>
                  </div>

                  <div className="stat-card revenue">
                    <h3>Total Revenue</h3>
                    <div className="stat-number">
                      {reportData.summary?.totalRevenue?.toLocaleString() || 0}<span className="currency">PKR</span>
                    </div>
                    <div className="stat-breakdown">
                      <span className="stat-item">
                        Total Bookings: {reportData.summary?.totalBookings || 0}
                      </span>
                    </div>
                    <div className="revenue-increase">
                      Avg: {reportData.summary?.averageRevenuePerSlot || 0} PKR per slot
                    </div>
                    {/* <p className="stat-note">Bookings: {reportData.summary?.totalBookings || 0}</p> */}
                  </div>

                
                    <div className="stat-card">
                    <h3>Top Pitch</h3>
                    <div className="stat-number">
                      {reportData.performanceMetrics?.topPerformingPitch?.name}
                    </div>
                    <div className="stat-breakdown">
                      <span className="stat-item">
                        {/* Total Pitches: {reportData.pitchBreakdown.length || 0} */}
                        Relevant sport: {reportData.performanceMetrics?.topPerformingSport?.name}
                      </span>
                    </div>
                    <p className="revenue-increase">Revenue: {reportData.performanceMetrics?.topPerformingPitch?.revenue || 0} PKR</p>
                  </div>
                </>
              ) : (
                // Default/empty state
                <>
                  <div className="stat-card">
                    <h3>Slots Booked</h3>
                    <div className="stat-number empty-state home">
                      <span className="empty-text">--</span>
                    </div>
                    <div className="stat-breakdown">
                      <span className="stat-item empty-item">Choose facility above</span>
                    </div>
                    <p className="stat-note">📅 Pick a date to see data</p>
                  </div>

                  <div className="stat-card">
                    <h3>Sports Booked</h3>
                    <div className="stat-number empty-state home">
                      <span className="empty-text">--</span>
                    </div>
                    <div className="sport-tags">
                      <span className="sport-tag empty-tag">
                        Select filters
                      </span>
                    </div>
                    <p className="stat-note">🏟️ Choose your facility first</p>
                  </div>

                  <div className="stat-card revenue">
                    <h3>Total Revenue</h3>
                    <div className="stat-number empty-state home">
                      <span className="empty-text">--</span>
                    </div>
                    <div className="revenue-increase empty-revenue">
                      💰 Revenue data will appear here
                    </div>
                    <p className="stat-note">📊 Get insights by selecting filters</p>
                  </div>

                  <div className="stat-card">
                    <h3>Revenue Per Sports</h3>
                    <div className="stat-number empty-state home">
                      <span className="empty-text">--</span>
                    </div>
                    <div className="sport-tags">
                      <span className="sport-tag empty-tag">
                        Ready to analyze
                      </span>
                    </div>
                    <p className="stat-note">📈 Performance metrics await</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>


      </div>
    </>
  );
};

export default Home;
